import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, Button } from '@mui/material';
import {
  useEnableCommunityMutation,
  useDisableCommunityMutation,
} from '../../../../generated/graphql';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal as StreamChatModal, ModalProps } from 'stream-chat-react';
import { sprinkles } from '@design-system/sprinkles.css';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';
import communityIphoneImage from '/static/images/community_iphone.png';

const Modal = StreamChatModal as unknown as React.FC<
  React.PropsWithChildren<ModalProps>
>;

const COMMUNITY_TOGGLE_CONFIG = {
  enable: {
    title: 'Ready for Community?',
    description:
      "Community is a great way to engage, retain subscribers, and build meaningful relationships with members. Click 'Learn more' to discover insights on building thriving communities.",
    actionText: 'Enable',
    actionButtonClass: 'h-[50px] w-full bg-primary text-white rounded-[100px]',
  },
  disable: {
    title: 'Are you sure?',
    description:
      'Your audience will no longer be able to interact with each other and discuss their progress, and you will be unable to facilitate these conversations.',
    actionText: 'Turn off',
    actionButtonClass: 'h-[50px] w-full bg-red-1 text-white rounded-[100px]',
  },
};

export type Influencer = {
  communityId?: number | null;
  id: number;
  offering: string[];
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  customizations?: {
    premiumFeaturesOTPIds?: number[];
  };
};

export type CommunityToggleModalProps = {
  isModalVisible: boolean;
  onDismiss: () => void;
  isCommunityEnabled: boolean;
  setCommunityEnabled: (value: boolean) => void;
  influencer: Influencer;
  setCommunityModalVisible: (value: boolean) => void;
};

export const CommunityToggleModal = ({
  isModalVisible,
  onDismiss,
  isCommunityEnabled,
  setCommunityEnabled,
  influencer,
  setCommunityModalVisible,
}: CommunityToggleModalProps) => {
  const { title, description, actionText, actionButtonClass } =
    COMMUNITY_TOGGLE_CONFIG[isCommunityEnabled ? 'disable' : 'enable'];
  const [isSubmitting, setSubmitting] = useState(false);

  const [enableCommunityMutation] = useEnableCommunityMutation({
    variables: {
      creatorId: influencer?.id,
      creatorEmail: String(influencer?.email),
      creatorName: `${influencer?.firstName} ${influencer?.lastName}`,
    },
  });

  const [disableCommunityMutation] = useDisableCommunityMutation({
    variables: {
      creatorId: influencer?.id,
    },
  });

  const onSubmit = () => {
    setSubmitting(true);
    if (isCommunityEnabled) {
      disableCommunityMutation()
        .then(() => {
          toast.success('Community disabled successfully');
          setCommunityModalVisible(false);
          setCommunityEnabled(false);
        })
        .catch(() => {
          toast.error('Something went wrong with disabling community');
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      enableCommunityMutation()
        .then(() => {
          toast.success('Community enabled successfully');
          setCommunityModalVisible(false);
          setCommunityEnabled(true);
        })
        .catch(() => {
          toast.error('Something went wrong with enabling community');
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const disabled =
    !isSubscriptionCreator(influencer.offering) &&
    !hasPremiumFeaturesOTPIds(influencer?.customizations);

  return (
    <Modal open={isModalVisible} onClose={onDismiss}>
      <Box className="w-full h-full flex justify-center items-center">
        <Box className="flex flex-col justify-center items-start w-[400px] h-auto p-5 bg-white rounded-[20px]">
          <Box className="h-4 w-full flex justify-end">
            <FontAwesomeIcon
              icon={faClose}
              className={`cursor-pointer`}
              onClick={onDismiss}
            />
          </Box>
          <Box className="w-full flex flex-col gap-5 items-center justify-center">
            <Box
              className="w-[200px] h-[200px] relative"
              sx={{
                '&::after': {
                  content: '""',
                  width: 200,
                  height: 80,
                  background:
                    'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)',
                  position: 'absolute',
                  bottom: -1,
                  left: 0,
                  zIndex: 1,
                },
              }}
            >
              <img
                src={communityIphoneImage}
                alt="community feature banner"
                className="w-[200px]"
              />
            </Box>
            <Box className="flex flex-col w-[300px]">
              <Typography
                align="center"
                variant="h4"
                className="font-extrabold"
              >
                {title}
              </Typography>
              <Typography align="center" className="text-[14px]">
                {description}
              </Typography>
            </Box>
            <Button
              className={actionButtonClass}
              onClick={onSubmit}
              disabled={isSubmitting || disabled}
            >
              {actionText}
            </Button>
            {disabled && (
              <span
                className={sprinkles({ color: 'ERROR-100', fontSize: 'xs' })}
              >
                Community is not available for OTP-only creators
              </span>
            )}
            {!isCommunityEnabled && (
              <Button
                href="https://playbookapp.io/handbook/community-tutorial-best-practices"
                target="_blank"
                variant="text"
              >
                Learn more
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
